import { Loading } from '@aon/aon-tws-common-ui';
import {
	HierarchyNodeActionCreator,
	HierarchySearch,
	HierarchySearchActionCreator,
	IHierarchyNodeActions,
	SelectHierarchy,
} from '@aon/tws-common-ui-hierarchy';
import { IHierarchySearchActionCreator } from '@aon/tws-common-ui-hierarchy/build/lib/store/HierarchySearchStore';
import { INodeSelectionCriteria } from '@aon/tws-common-ui-hierarchy/build/lib/types';
import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { LoadingWrapper } from '../../components/common';
import Constants from '../../constants';
import { ICommonHierarchyActionCreators } from '../../models';
import { IApplicationState } from '../../store';
import { ActionCreators as CommonHierarchyActionCreators, TCommonHierarchyState } from '../../store/CommonHierarchyStore';

// makes the white background transparent.
const TransparentLoading = styled(Loading)`
	mix-blend-mode: multiply;
`;

interface IReadonlyHierarchyProps {
	hierarchyId?: string;
	includeOwners: boolean;
	selectionCriteria: INodeSelectionCriteria | undefined;
	hideCheckboxes?: boolean;

	whenDataNotAvailable?: Partial<{
		render: () => JSX.Element;
		handle: () => void;
	}>;
}

export type ReadonlyHierarchyProps = IReadonlyHierarchyProps &
	IHierarchyNodeActions &
	IHierarchySearchActionCreator & { hierarchy: TCommonHierarchyState } & ICommonHierarchyActionCreators;

export class ReadonlyHierarchyContent extends React.PureComponent<ReadonlyHierarchyProps> {
	public async componentDidMount() {
		const { hierarchyId, hierarchy: hierarchyInfo, includeOwners } = this.props;

		// If a hierarchy Id was supplied, and the info for the corresponding hierarchy isn't present
		if (
			hierarchyId &&
			(hierarchyInfo.isLoading || hierarchyInfo.couldNotFetchData || hierarchyInfo.hierarchyHeader?.hierarchyMetadataId !== hierarchyId)
		) {
			// Fetch the hierarchy info
			this.props.GetHierarchyVm(hierarchyId, includeOwners);
		}
	}

	public async componentDidUpdate(prevProps: ReadonlyHierarchyProps) {
		const { hierarchyId, includeOwners } = this.props;

		// If a hierarchy Id was supplied, and the request parameters for the hierarchy were changed
		if (hierarchyId && (prevProps.hierarchyId !== hierarchyId || prevProps.includeOwners !== includeOwners)) {
			// Fetch the hierarcy info
			this.props.GetHierarchyVm(hierarchyId, includeOwners);
		}

		// Determine whether or not hierarchy data was fetched successfully.
		const { hierarchy: currHierarchyState } = this.props;
		const { hierarchy: prevHierarchyState } = prevProps;

		if (prevHierarchyState.isLoading && !currHierarchyState.isLoading && currHierarchyState.couldNotFetchData) {
			this.props.whenDataNotAvailable?.handle?.();
		}
	}

	public render() {
		const { hierarchy } = this.props;

		// If hierarchy data could not be fetched
		if (!hierarchy.isLoading && hierarchy.couldNotFetchData) {
			return this.props.whenDataNotAvailable?.render?.() ?? null;
		}

		// If hierarchy header info is not present
		if (!hierarchy.hierarchyHeader) {
			// If in the process of fetching info
			if (hierarchy.isLoading) {
				return (
					<LoadingWrapper>
						<TransparentLoading height='70px' width='70px' />
					</LoadingWrapper>
				);
			}

			return null;
		}

		return (
			<>
				<HierarchySearch {...this.props} {...hierarchy.hierarchySearchState} />
				<SelectHierarchy
					{...this.props}
					{...hierarchy}
					disabled={true}
					enableDismiss={false}
					hideClearButton={true}
					hideSelectType={true}
					showOwners={this.props.includeOwners}
					onChangeSelectionCriteria={() => {
						/** */
					}}
				/>
			</>
		);
	}
}

const hierarchyNodeActionCreators = new HierarchyNodeActionCreator(Constants.ORGMANAGER_HIERARCHIES_URL, true);
const hierarchySearchActionCreators = new HierarchySearchActionCreator(hierarchyNodeActionCreators, Constants.ORGMANAGER_HIERARCHIES_URL);

/**
 * This component renders a readonly select hierarchy that connects
 * to hierarchy state using redux and the default store.
 */
export const ReadonlyHierarchy: React.ComponentType<IReadonlyHierarchyProps> = connect(
	(state: IApplicationState, props: { includeOwners: boolean }) => ({
		...props,
		hierarchy: state.Hierarchy,
	}),
	{
		...hierarchyNodeActionCreators,
		...hierarchySearchActionCreators,
		...CommonHierarchyActionCreators,
	},
)(ReadonlyHierarchyContent);
